export interface ListCachedEntry<T> {
  data: T;
  timestamp: number;
}

export interface ListCachePendingEntry<T> {
  promise: Promise<T>;
  timestamp: number;
}

export type ListCacheEntry<T> = ListCachedEntry<T> | ListCachePendingEntry<T>;

export interface ListCacheOptions<T, F> {
  idField: keyof T;
  fetchData: (ids: string[]) => Promise<F[]>;
  formatData?: (data: F) => T;
  cleanupInterval?: number;
}

export class ListCache<T, F> {
  private cacheMap: Record<string, ListCacheEntry<T>> = {};
  private cleanupTimer: any = null;
  private readonly DEFAULT_CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private options: ListCacheOptions<T, F>;

  get cleanupInterval() {
    return this.options.cleanupInterval ?? this.DEFAULT_CLEANUP_INTERVAL;
  }

  constructor(options: ListCacheOptions<T, F>) {
    this.options = options;
    this.startCacheCleanup();
  }

  getData(ids: string[], force: boolean = false) {
    return this.getWithCache(ids, force);
  }

  getCacheData(id: string) {
    return (this.cacheMap[id] as ListCachedEntry<T>)?.data;
  }

  updateCache(dataList: F[]) {
    dataList.forEach((item) => {
      const data = this.options.formatData?.(item) ?? (item as unknown as T);
      // const cachedEntry = this.cacheMap[this.getId(data)];

      //if (cachedEntry) {
      this.cacheMap[this.getId(data)] = {
        data,
        timestamp: Date.now(),
      };
      // }
    });
  }

  deleteCache(ids: string[]): void {
    ids.forEach((id) => {
      delete this.cacheMap[id];
    });
  }

  /**
   * Manually clear all cache
   */
  clearCache(): void {
    this.cacheMap = {};
  }

  /**
   * Dispose resources and cleanup timer
   */
  dispose(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clearCache();
  }

  private getId(data: T) {
    return data[this.options.idField] as string;
  }

  /**
   * Check if cache entry is a resolved user data
   */
  private isCached(entry: ListCacheEntry<T>): entry is ListCachedEntry<T> {
    return 'data' in entry;
  }

  /**
   * Check if cache entry is a pending request
   */
  private isPending(entry: ListCacheEntry<T>): entry is ListCachePendingEntry<T> {
    return 'promise' in entry;
  }

  /**
   * Resolve dataList with cache support (including pending requests)
   */
  private async getWithCache(ids: string[], force: boolean = false): Promise<T[]> {
    const results: (T | Promise<T>)[] = [];
    const missingIds: string[] = [];

    // Check cache for each user
    for (const id of ids) {
      const cachedEntry = this.cacheMap[id];

      if (cachedEntry && !force) {
        if (this.isCached(cachedEntry)) {
          // T data is already resolved
          results.push(cachedEntry.data);
        } else if (this.isPending(cachedEntry)) {
          // Request is pending, reuse the promise
          results.push(cachedEntry.promise);
        }
      } else {
        // No cache entry, collect for batch request
        missingIds.push(id);
        results.push(null as any); // Placeholder, will be replaced
      }
    }

    // If there are missing dataList, create a batch request
    if (missingIds.length > 0) {
      const batchPromise = this.createBatchRequest(missingIds);

      // Replace placeholders with promises from batch request
      for (let i = 0; i < results.length; i++) {
        if (results[i] === null) {
          const id = ids[i];
          results[i] = batchPromise.then((dataList) => {
            return dataList.find((user) => this.getId(user) === id)!;
          });
        }
      }
    }

    // Wait for all promises to resolve
    const allData = await Promise.all(results);

    // Return dataList in the same order as input
    return allData;
  }

  /**
   * Create a batch request for multiple dataList and cache individual promises
   */
  private createBatchRequest(ids: string[]): Promise<T[]> {
    const batchPromise = this.fetchData(ids).catch((error) => {
      // Remove failed requests from cache
      ids.forEach((id) => {
        delete this.cacheMap[id];
      });
      throw error;
    });

    // Cache individual promises for each user
    ids.forEach((id) => {
      const dataPromise = batchPromise
        .then((dataList) => {
          const data = dataList.find((u) => this.getId(u) === id);
          if (!data) {
            throw new Error(`T ${id} not found in batch response`);
          }

          // Convert pending request to cached user
          this.cacheMap[id] = {
            data,
            timestamp: Date.now(),
          };

          return data;
        })
        .catch((error) => {
          // Remove failed request from cache
          delete this.cacheMap[id];
          throw error;
        });

      // Cache the pending promise
      this.cacheMap[id] = {
        promise: dataPromise,
        timestamp: Date.now(),
      };
    });

    return batchPromise;
  }

  /**
   * Fetch dataList from server
   */
  private async fetchData(ids: string[]): Promise<T[]> {
    const dataList = ((await this.options.fetchData(ids)) || []) as unknown as T[];
    const now = Date.now();
    const formatData = this.options.formatData;

    // Cache the resolved dataList
    dataList.forEach((data) => {
      this.cacheMap[this.getId(data)] = {
        data: formatData?.(data as unknown as F) ?? data,
        timestamp: now,
      };
    });

    return dataList;
  }

  /**
   * Start cache cleanup timer
   */
  private startCacheCleanup(): void {
    const interval = this.cleanupInterval;

    this.cleanupTimer = setInterval(() => {
      this.cleanupExpiredCache();
    }, interval);
  }

  /**
   * Clean up expired cache items
   * @param maxAge Maximum cache age in milliseconds, defaults to cleanup interval
   */
  private cleanupExpiredCache(maxAge?: number): void {
    const now = Date.now();
    const interval = this.cleanupInterval;
    const expireTime = maxAge ?? interval;

    for (const [id, cacheEntry] of Object.entries(this.cacheMap)) {
      if (now - cacheEntry.timestamp > expireTime) {
        delete this.cacheMap[id];
      }
    }
  }
}
